import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { applyMiddleware, compose, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';
import App from './App';
import './locales/i18n';
import reportWebVitals from './reportWebVitals';
import { rootReducer } from './states';

// Warm the App Bridge v4 session token at the earliest possible moment (the
// CDN script in <head> has long since defined window.shopify by the time the
// bundle runs). The first idToken() triggers the parent-frame handshake; every
// later caller (fetchInstance/api layers) then resolves from v4's cache in
// single-digit ms instead of paying it on the LCP-critical bootstrap request.
window.shopify?.idToken?.().catch(() => {});

// DevTools wiring only outside production — no reason to ship the extension
// bridge (and its per-action serialization overhead) to merchants.
const composeEnhancers =
  process.env.NODE_ENV === 'production' ? compose : composeWithDevTools({});
const store = createStore(rootReducer, composeEnhancers(applyMiddleware(thunk)));
createRoot(document.getElementById('root')).render(
  <Provider store={store}>
    <App />
  </Provider>
);

// function sendToGoogleAnalytics({ name, delta, value, id, entries }) {    tesst
//   window.gtag('event', name, {
//     // Built-in params:
//     value: delta, // Use `delta` so the value can be summed.
//     // Custom params:
//     metric_id: id, // Needed to aggregate events.
//     metric_value: value, // Value for querying in BQ
//     metric_delta: delta, // Delta for querying in BQ
//   });
// }

// Per-metric attribution fields (web-vitals/attribution build) — lets the
// console line point straight at the offending element/interaction instead
// of just a number. Field names come from each metric's *Attribution type.
function attributionFields(metric) {
  const a = metric.attribution || {};
  switch (metric.name) {
    case 'CLS':
      return {
        target: a.largestShiftTarget,
        shiftValue: a.largestShiftValue,
        shiftTime: a.largestShiftTime,
        loadState: a.loadState,
      };
    case 'LCP':
      return {
        target: a.element,
        url: a.url,
        timeToFirstByte: a.timeToFirstByte,
        resourceLoadDelay: a.resourceLoadDelay,
        resourceLoadTime: a.resourceLoadTime,
        elementRenderDelay: a.elementRenderDelay,
      };
    case 'INP':
      return {
        target: a.interactionTarget,
        interactionType: a.interactionType,
        inputDelay: a.inputDelay,
        processingDuration: a.processingDuration,
        presentationDelay: a.presentationDelay,
      };
    default:
      return {};
  }
}

// `target` above is a CSS selector string, not a live node — by the time
// anyone re-queries it by hand in the console, a swapped-out/loading→loaded
// element is usually already gone. Resolve it right now, at report time,
// and capture its own text so the log is self-contained.
function describeTarget(selector) {
  if (!selector || typeof document === 'undefined') return {};
  let el;
  try {
    el = document.querySelector(selector);
  } catch {
    return {};
  }
  if (!el) return { targetText: '(not in DOM anymore)' };
  return {
    targetText: el.textContent?.trim().slice(0, 120),
    targetHTML: el.outerHTML?.slice(0, 200),
  };
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals((metric) => {
  if (!['CLS', 'LCP', 'INP'].includes(metric.name)) return;
  const fields = { ...attributionFields(metric) };
  Object.assign(fields, describeTarget(fields.target));
  // Dev: see exactly which element/interaction is behind the number, live,
  // in the console — no need to open the Performance panel for every check.
  // eslint-disable-next-line no-console
  console.log(`[WebVitals] ${metric.name}`, metric.value, fields);
  // Prod: forward to GTM/whatever picks up dataLayer events, so real
  // merchant sessions get measured too, not just local devtools runs.
  // LCP's breakdown (timeToFirstByte/resourceLoadDelay/resourceLoadTime/
  // elementRenderDelay) used to only reach the dev console via
  // attributionFields() above — nothing durable captured WHY real sessions'
  // LCP was slow, only the final number and which element. Forwarding the
  // same breakdown here lets it be queried across real sessions instead of
  // re-measured by hand one devtools tab at a time.
  window.dataLayer?.push({
    event: 'web_vitals',
    metric_name: metric.name,
    metric_value: Math.round(metric.value * (metric.name === 'CLS' ? 1000 : 1)),
    metric_target: fields.target,
    page_path: window.location.pathname,
    ...(metric.name === 'LCP' && {
      metric_time_to_first_byte: fields.timeToFirstByte,
      metric_resource_load_delay: fields.resourceLoadDelay,
      metric_resource_load_time: fields.resourceLoadTime,
      metric_element_render_delay: fields.elementRenderDelay,
    }),
  });
});
